/**
 * CustomRangeControl — custom date-range picker for the statistics page (#550).
 *
 * Augments the 24h/7d/30d preset control (TimeRangeControl) with an arbitrary
 * primary window and an optional custom comparison window. Uses native
 * <input type="date"> fields inside a Radix popover (no calendar dependency).
 * The comparison window auto-derives to the immediately-prior equal-length period
 * unless the user opts to customize it.
 */
import { useState } from 'react';
import { CalendarRange, ChevronRight, GitCompareArrows } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/** A resolved custom range. Dates are ISO datetimes; `end`/`compareEnd` are exclusive. */
export interface CustomRange {
  start: string;
  end: string;
  compareStart?: string;
  compareEnd?: string;
}

interface CustomRangeControlProps {
  /** Whether a custom range is the active selection (styles the trigger). */
  active: boolean;
  onApply: (range: CustomRange) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// A native date input yields 'YYYY-MM-DD'. Treat it as a UTC calendar day.
const startISO = (d: string) => `${d}T00:00:00.000Z`;
// Exclusive upper bound: advance one UTC day so the chosen end date is fully included.
const endExclusiveISO = (d: string) => {
  const dt = new Date(`${d}T00:00:00.000Z`);
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString();
};
const fmtUTC = (dt: Date) => `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;

export function CustomRangeControl({ active, onApply }: CustomRangeControlProps) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [showCompare, setShowCompare] = useState(false);
  const [compareStart, setCompareStart] = useState('');
  const [compareEnd, setCompareEnd] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Auto-derived prior equal-length window, for the helper text.
  const derivedLabel = (() => {
    if (!start || !end) return null;
    const s = new Date(startISO(start));
    const span = new Date(endExclusiveISO(end)).getTime() - s.getTime();
    const priorStart = new Date(s.getTime() - span);
    const priorEndIncl = new Date(s.getTime() - 86_400_000); // day before the primary start
    return `${fmtUTC(priorStart)} – ${fmtUTC(priorEndIncl)}`;
  })();

  const handleApply = () => {
    if (!start || !end) {
      setError('Pick both a start and end date.');
      return;
    }
    if (start > end) {
      // Lexicographic compare is chronological for YYYY-MM-DD.
      setError('Start must be on or before end.');
      return;
    }
    const range: CustomRange = { start: startISO(start), end: endExclusiveISO(end) };
    if (showCompare && compareStart && compareEnd) {
      if (compareStart > compareEnd) {
        setError('Comparison start must be on or before comparison end.');
        return;
      }
      range.compareStart = startISO(compareStart);
      range.compareEnd = endExclusiveISO(compareEnd);
    }
    setError(null);
    onApply(range);
    setOpen(false);
  };

  const triggerLabel = active && start && end
    ? `${fmtUTC(new Date(startISO(start)))} – ${fmtUTC(new Date(startISO(end)))}`
    : 'Custom';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-haspopup="dialog"
          className={cn(active && 'border-primary ring-1 ring-primary')}
        >
          <CalendarRange className="h-4 w-4 mr-2" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <p className="text-sm font-semibold">Custom range</p>
        <p className="text-xs text-muted-foreground">Pick the period to analyze.</p>

        <div className="mt-3 flex gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="stats-range-start" className="text-xs text-muted-foreground">Start</Label>
            <Input
              id="stats-range-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="flex-1 space-y-1">
            <Label htmlFor="stats-range-end" className="text-xs text-muted-foreground">End</Label>
            <Input
              id="stats-range-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            aria-expanded={showCompare}
            onClick={() => setShowCompare((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold"
          >
            <ChevronRight
              className={cn('h-4 w-4 text-muted-foreground transition-transform', showCompare && 'rotate-90')}
            />
            Compare to…
          </button>
          <div className="mt-2">
            <div className="flex items-center gap-2 rounded-md bg-muted px-2.5 py-2 text-xs text-muted-foreground">
              <GitCompareArrows className="h-3.5 w-3.5 shrink-0" />
              <span>
                Prior period
                {derivedLabel ? <> — <span className="text-foreground">{derivedLabel}</span></> : null} (auto)
              </span>
            </div>
            {showCompare && (
              <div className="mt-2 flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="stats-compare-start" className="text-xs text-muted-foreground">Compare start</Label>
                  <Input
                    id="stats-compare-start"
                    type="date"
                    value={compareStart}
                    onChange={(e) => setCompareStart(e.target.value)}
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="stats-compare-end" className="text-xs text-muted-foreground">Compare end</Label>
                  <Input
                    id="stats-compare-end"
                    type="date"
                    value={compareEnd}
                    onChange={(e) => setCompareEnd(e.target.value)}
                  />
                </div>
              </div>
            )}
            {!showCompare && (
              <button
                type="button"
                onClick={() => setShowCompare(true)}
                className="mt-1.5 text-xs font-medium text-primary"
              >
                Customize comparison range
              </button>
            )}
          </div>
        </div>

        {error && <p className="mt-3 text-xs text-destructive">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={handleApply}>Apply</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
